import { useState, useEffect, useMemo } from "react";

const MATERIAL_ATTR_NAME = "Material of Construction";

/**
 * useProductConfigurator
 * ------------------------------------------------------------------
 * This hook powers the "pick a product by filters" screen.
 *
 * Given a list of sub-product-groups (each containing products, and
 * each product containing spec attributes), it gives you:
 *
 *  1. The list of filter columns to show (Series, Material, etc.)
 *  2. The available pill options for each column, based on what's
 *     still possible given the current selections
 *  3. The single matching product, once selections narrow it down
 *     to exactly one
 *
 * HOW "OPTIONS" WORK:
 *   Every column's options are always calculated fresh from the full
 *   product list filtered by selections — nothing is pre-computed or
 *   cached, so the pills always reflect reality.
 *
 *   A column disappears entirely if it has zero *selectable* options
 *   for the current selections (i.e. every value for that attribute
 *   is disabled given what's already picked) — not merely zero
 *   values anywhere in the dataset. This is what makes irrelevant
 *   attributes (e.g. "Miter Type" while TYPE=Curved is selected)
 *   disappear instead of lingering with everything disabled.
 * ------------------------------------------------------------------
 */
export default function useProductConfigurator(subProductGroups) {
    const [allColDefs, setAllColDefs] = useState([]);
    const [selections, setSelections] = useState({});
    const [matchedProduct, setMatchedProduct] = useState(null);
    const [isFromSearch, setIsFromSearch] = useState(false);

    useEffect(() => {
        if (!subProductGroups?.length) return;
        setAllColDefs(buildColumnDefinitions(subProductGroups));
        setSelections({});
        setMatchedProduct(null);
        setIsFromSearch(false);
    }, [subProductGroups]);

    const flatProductsCache = useMemo(() => new WeakMap(), []);

    const getFlatProducts = (groups) => {
        if (flatProductsCache.has(groups)) return flatProductsCache.get(groups);
        const flat = groups.flatMap((group) =>
            (group.products ?? []).map((product) => ({
                ...product,
                subGroupId: group.id,
                subGroupName: group.subGroup,
                shortCode: group.shortCode,
            }))
        );
        flatProductsCache.set(groups, flat);
        return flat;
    };

    const getAttributeValueEntries = (product, attributeName) => {
        const entries = [];
        product.productSpecGroupMaps?.forEach((specGroup) => {
            specGroup.productSpecMaps?.forEach((specMap) => {
                if (specMap.attributeName !== attributeName) return;
                if (!specMap.isActive) return;
                specMap.productSpecValueMaps?.forEach((valueMap) => {
                    const isUsable = valueMap.isActive && valueMap.valueName;
                    if (!isUsable) return;
                    entries.push({ value: valueMap.valueName, sequence: valueMap.sequence });
                });
            });
        });
        return entries;
    };

    const getAttributeValues = (product, attributeName) => {
        const values = [];
        getAttributeValueEntries(product, attributeName).forEach(({ value }) => {
            if (!values.includes(value)) values.push(value);
        });
        return values;
    };

    /* ════════════════════════════════════════════════════════════
       SECTION 2: BUILDING THE COLUMN LIST (Series, Material, Spec...)
       ════════════════════════════════════════════════════════════ */

    // Scans every product in every group to find which attributes are
    // actually usable as filters ("isSelectionParameter"), and in what
    const buildColumnDefinitions = (groups) => {
        const attributeSequenceMap = {};

        groups.forEach((group) => {
            group.products?.forEach((product) => {
                product.productSpecGroupMaps?.forEach((specGroup) => {
                    specGroup.productSpecMaps?.forEach((specMap) => {
                        const isValidFilterAttribute =
                            specMap.isActive && specMap.isSelectionParameter;
                        if (!isValidFilterAttribute) return;

                        const attrName = specMap.attributeName;
                        if (attributeSequenceMap[attrName] === undefined) {
                            attributeSequenceMap[attrName] = specMap.sequence;
                        }
                    });
                });
            });
        });

        return Object.entries(attributeSequenceMap)
            .sort((a, b) => a[1] - b[1])
            .map(([attributeName]) => ({
                key: `spec__${attributeName}`,
                label: attributeName,
                attrName: attributeName,
            }));
    };
    /* ════════════════════════════════════════════════════════════
       SECTION 3: FILTERING PRODUCTS BASED ON CURRENT SELECTIONS
       ════════════════════════════════════════════════════════════ */

    // Checks if a single product satisfies every selection that's been made.
    // This is the ONE place that knows how to compare a product against
    // selections — every other function below reuses it instead of
    // re-implementing the same checks.
    const productMatchesSelections = (product, currentSelections) => {
        for (const [columnKey, selectedValue] of Object.entries(currentSelections)) {
            if (!selectedValue) continue; // nothing picked for this column, skip it

            const attributeName = columnKey.replace("spec__", "");
            const values = getAttributeValues(product, attributeName);
            if (!values.includes(selectedValue)) return false;
        }
        return true;
    };

    const getProductsMatchingSelections = (groups, currentSelections) =>
        getFlatProducts(groups).filter((product) => productMatchesSelections(product, currentSelections));


    const getOptions = (columnDef, currentSelections, groups) => {
        const selectionsExcludingThisColumn = { ...currentSelections };
        delete selectionsExcludingThisColumn[columnDef.key];

        const allProducts = getFlatProducts(groups);
        const eligibleProducts = getProductsMatchingSelections(groups, selectionsExcludingThisColumn);

        const extractValueEntries = (product) => getAttributeValueEntries(product, columnDef.attrName);

        const allValues = new Set();
        const valueSequenceMap = new Map();

        allProducts.forEach((product) => {
            extractValueEntries(product).forEach(({ value, sequence }) => {
                allValues.add(value);
                const existingSequence = valueSequenceMap.get(value);
                if (existingSequence === undefined || sequence < existingSequence) {
                    valueSequenceMap.set(value, sequence);
                }
            });
        });

        const validValues = new Set();
        eligibleProducts.forEach((product) =>
            extractValueEntries(product).forEach(({ value }) => validValues.add(value))
        );

        return [...allValues]
            .sort((a, b) => {
                const seqA = valueSequenceMap.get(a);
                const seqB = valueSequenceMap.get(b);
                const safeSeqA = seqA === undefined ? Infinity : seqA;
                const safeSeqB = seqB === undefined ? Infinity : seqB;
                return safeSeqA - safeSeqB;
            })
            .map((value) => ({
                value,
                label: value,
                disabled: !validValues.has(value),
            }));
    };

    const getVisibleColumnStates = (groups) => {
        const visibleColumns = [];

        allColDefs.forEach((columnDef) => {
            const options = getOptions(columnDef, selections, groups);

            const hasSelectableOption = options.some((opt) => !opt.disabled);
            if (!hasSelectableOption) return;

            const selectedVal = selections[columnDef.key];

            if (isFromSearch && matchedProduct && !selectedVal) return;

            visibleColumns.push({
                col: columnDef,
                opts: options,
                selectedVal,
                isLocked: false,
                isSingleOption: options.length === 1,
            });
        });

        return visibleColumns;
    };

    const findMatch = (currentSelections, groups) => {
        const candidates = getProductsMatchingSelections(groups, currentSelections);
        return candidates.length === 1 ? candidates[0] : null;
    };

    const countMatches = (currentSelections, groups) =>
        getProductsMatchingSelections(groups, currentSelections).length;

    const getMatchingProducts = (currentSelections, groups) => {
        const matches = getProductsMatchingSelections(groups, currentSelections);
        const seenIds = new Set();
        return matches.filter((product) => {
            if (seenIds.has(product.id)) return false;
            seenIds.add(product.id);
            return true;
        });
    };


    const handlePillClick = (columnDef, value, groups) => {
        const wasAlreadySelected = selections[columnDef.key] === value;
        const updatedSelections = { ...selections };

        if (wasAlreadySelected) {
            delete updatedSelections[columnDef.key];
        } else {
            updatedSelections[columnDef.key] = value;
        }

        const changedColumnIndex = allColDefs.findIndex((c) => c.key === columnDef.key);
        const laterColumns = allColDefs.slice(changedColumnIndex + 1);

        laterColumns.forEach((laterColumn) => {
            const currentValue = updatedSelections[laterColumn.key];
            if (!currentValue) return;

            const optionsForLaterColumn = getOptions(laterColumn, updatedSelections, groups);
            const matchingOption = optionsForLaterColumn.find((opt) => opt.value === currentValue);
            const isStillValid = matchingOption && !matchingOption.disabled;

            if (!isStillValid) {
                delete updatedSelections[laterColumn.key];
            }
        });

        setSelections(updatedSelections);
        setMatchedProduct(findMatch(updatedSelections, groups));
        setIsFromSearch(false);
    };

    const buildSelectionsFromProduct = (product) => {
        const selectionsFromProduct = {};
        allColDefs.forEach((columnDef) => {
            const values = getAttributeValues(product, columnDef.attrName);
            if (values.length > 0) selectionsFromProduct[columnDef.key] = values[0];
        });
        return selectionsFromProduct;
    };

    const selectFromSearchResult = (product, groups) => {
        const newSelections = buildSelectionsFromProduct(product);
        setSelections(newSelections);
        setMatchedProduct(product);
        setIsFromSearch(true);
    };

    const clearSelections = () => {
        setSelections({});
        setMatchedProduct(null);
        setIsFromSearch(false);
    };

    const breadcrumbParts = allColDefs
        .map((columnDef) => selections[columnDef.key])
        .filter((value) => value && value.toLowerCase() !== "none");

    const hasAny = Object.values(selections).some(Boolean);

    return {
        allColDefs,
        selections,
        matchedProduct,
        breadcrumbParts,
        hasAny,
        getFlatProducts,
        getOptions,
        getVisibleColumnStates,
        handlePillClick,
        selectFromSearchResult,
        clearSelections,
        countMatches,
        getMatchingProducts,
    };
}